import 'date-fns';
import React, {useEffect} from 'react';
import {Box, Button, Grid, Typography, makeStyles, TextField} from '@material-ui/core';
import {observer} from 'mobx-react-lite';
import {ParameterStore} from '@/Core/mobx/ParameterStore/ParameterStore';
import InputText from '../../../UI/EmcApplicationQuestions/inputs/core/InputText';
import {z} from 'zod';
import {Controller, useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {useCommonModalStyles} from '@/Agent/Quotes/InstantAppModalPages/common/modalStyles';
import csx from 'classnames';
import FixedNavigation from '@/Agent/Quotes/InstantAppModalPages/common/FixedNavigation';
import StickyTopNavigation from '@/Agent/Quotes/InstantAppModalPages/common/StickyTopNavigation';
import {ContactFormStore} from '@/Agent/ContactForm/ContactFormStore/ContactFormStore';
import {Autocomplete} from '@material-ui/lab';
import {AGENIUM_OCUPATION_LIST} from '@/Agent/constants';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {EMPLOYER_OPTIONAL_OCCUPATIONS} from '@/common/constants';

interface EmployerProps {
    parameterStore: ParameterStore;
    contactFormStore: ContactFormStore;
    onExit: () => void;
    onNext: () => void;
    onBack?: () => void;
}

const useStyles = makeStyles(() => ({
    buttonWrapper: {
        display: 'flex',
        columnGap: '24px',
    },
    btn: {
        fontFamily: 'FredokaRegular',
        fontWeight: 400,
        fontSize: '28px',
        lineHeight: '140%',
        borderWidth: '1.75px',
        borderRadius: '8px',
        padding: '20px',
        maxWidth: 337,
        width: '100%',
        color: '#174943',
        textTransform: 'none',
        borderColor: '#174943',
        '&:hover': {
            borderWidth: '1.75px',
            borderColor: '#174943',
            backgroundColor: 'rgba(90, 226, 136, 0.04)',
        },
    },
    activeBtn: {
        backgroundColor: '#EDFCF2',
    },
    container: {
        maxWidth: 700,
        width: '100%',
        position: 'relative',
        paddingBottom: 113,
        margin: '0 auto',
    },
    title: {
        marginBottom: 40,
        marginTop: 40,
        fontFamily: 'LoraSemiBold',
        fontStyle: 'normal',
        fontWeight: 600,
        fontSize: 40,
        lineHeight: '110%',
        color: '#3F51B5',
        opacity: 0.9,
    },
    form: {
        display: 'flex',
        rowGap: 24,
    },
}));

const formSchema = z
    .object({
        employmentStatus: z.boolean().refine((value) => value || !value),
        occupation: z
            .string()
            .min(1, 'Occupation is required')
            .nullable()
            .refine((val) => val !== null && val !== '', {
                message: 'Occupation is required',
            }),
        nameOfEmployer: z.string(),
    })
    .superRefine((data, ctx) => {
        if (data?.occupation && !EMPLOYER_OPTIONAL_OCCUPATIONS.includes(data.occupation) && !data.nameOfEmployer) {
            ctx.addIssue({
                path: ['nameOfEmployer'],
                code: z.ZodIssueCode.custom,
                message: 'Name of employer is required',
            });
        }
    });

const Employer: React.FC<EmployerProps> = observer(({onNext, onBack, onExit, parameterStore, contactFormStore}) => {
    const classes = useStyles();
    const {approval, onValueChangedPreApproval, prequal} = parameterStore;
    const commonClasses = useCommonModalStyles();
    const {
        control,
        formState: {isSubmitting, isValid},
        handleSubmit,
        trigger,
        watch,
    } = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: {
            employmentStatus:
                approval.currentlyEmployed.value === 'Employed' ||
                prequal.currentlyEmployed.value === 'Employed' ||
                false,
            occupation: AGENIUM_OCUPATION_LIST.includes(approval.occupation.value || prequal.occupation.value)
                ? approval.occupation.value || prequal.occupation.value
                : '',
            nameOfEmployer: approval.nameOfEmployer.value || prequal.nameOfEmployer.value || '',
        },
    });

    const occupation = watch('occupation');

    useEffect(() => {
        trigger('nameOfEmployer');
    }, [occupation]);

    const onSubmit = (data: any) => {
        onValueChangedPreApproval({...approval.nameOfEmployer, name: 'nameOfEmployer'}, data.nameOfEmployer);
        onValueChangedPreApproval({...approval.occupation, name: 'occupation'}, data.occupation);
        onValueChangedPreApproval(
            {...approval.currentlyEmployed, name: 'currentlyEmployed'},
            data.employmentStatus ? 'Employed' : null,
        );
        contactFormStore.saveForm();
        onNext();
    };

    return (
        <Box className={classes.container}>
            <StickyTopNavigation onExit={onExit} />
            <Typography className={classes.title}>Do you have an employer?</Typography>
            <Grid container spacing={2} component="form" onSubmit={handleSubmit(onSubmit)} className={classes.form}>
                <Grid item xs={12} classes={{root: classes.buttonWrapper}}>
                    <Controller
                        name="employmentStatus"
                        control={control}
                        render={({field}) => (
                            <>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    classes={{
                                        root: csx(classes.btn, {
                                            [classes.activeBtn]: field.value === true,
                                        }),
                                    }}
                                    onClick={() => field.onChange(true)}
                                >
                                    Yes
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    classes={{
                                        root: csx(classes.btn, {
                                            [classes.activeBtn]: field.value === false,
                                        }),
                                    }}
                                    onClick={() => field.onChange(false)}
                                >
                                    No
                                </Button>
                            </>
                        )}
                    />
                </Grid>
                <Grid item xs={6}>
                    <Typography className={commonClasses.label}>Occupation</Typography>
                    <Controller
                        name="occupation"
                        control={control}
                        render={({field, fieldState: {error}}) => (
                            <Autocomplete
                                {...field}
                                clearOnBlur
                                // disableClearable
                                options={AGENIUM_OCUPATION_LIST}
                                value={field.value || null}
                                onChange={(_, newValue) => field.onChange(newValue)}
                                filterOptions={(options, state) => {
                                    const results = options.filter((v) =>
                                        v.toLowerCase().includes(state.inputValue.toLowerCase()),
                                    );
                                    return results.length ? results : ['Other Occupation'];
                                }}
                                className={commonClasses.autocomplete}
                                popupIcon={<ExpandMoreIcon />}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        aria-label="Occupation"
                                        variant="outlined"
                                        error={!!error}
                                        classes={{root: commonClasses.input}}
                                        helperText={error?.message}
                                    />
                                )}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={6}>
                    <Typography className={commonClasses.label}>Employer Name</Typography>
                    <Controller
                        name={'nameOfEmployer'}
                        control={control}
                        render={({field, fieldState: {error}}) => (
                            <InputText
                                {...field}
                                aria-label="Employer Name"
                                placeholder="Employer Name"
                                error={!!error}
                                helperText={error?.message}
                                classes={{root: commonClasses.input}}
                            />
                        )}
                    />
                </Grid>
                <FixedNavigation onBack={onBack} isNextDisabled={!isValid} isLoading={isSubmitting} />
            </Grid>
        </Box>
    );
});

export default Employer;
